import { useMemo, useSyncExternalStore, type MouseEvent } from 'react'

/**
 * Where in MyLib the browser currently is.
 *
 * An area and, below it, whatever that area uses to say what is open inside it. The frame only
 * cares about the depth: anything below the area root is something you can come back out of, and
 * the back control in the bar removes the last step. What the steps mean is the area's business.
 */
export interface Route {
  readonly areaId: string
  readonly path: readonly string[]
}

/**
 * The address lives in the fragment.
 *
 * MyLib is served as one page from one address; paths of their own would need whatever serves the
 * built bundle to know which of them belong to the application and rewrite them, and that is not
 * decided here. The fragment is bookmarkable, survives a reload, gives the browser's own back
 * button something to do, and needs nothing of the server. What comes back from a finished sign-in
 * arrives as a query parameter and is untouched by any of this.
 */
export function hrefFor(areaId: string, path: readonly string[] = []): string {
  const segments = [areaId, ...path].map(encodeURIComponent)
  return `#/${segments.join('/')}`
}

export function parseRoute(
  hash: string,
  knownAreaIds: readonly string[],
  fallbackAreaId: string,
): Route {
  const segments = hash
    .replace(/^#/, '')
    .split('/')
    .filter((segment) => segment.length > 0)
    .map(decodeSegment)

  const [areaId, ...path] = segments

  if (areaId === undefined || !knownAreaIds.includes(areaId)) {
    return { areaId: fallbackAreaId, path: [] }
  }

  return { areaId, path }
}

/** A malformed escape is somebody's typed address, not a reason to fail. */
function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment)
  } catch {
    return segment
  }
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener('hashchange', onChange)
  return () => {
    window.removeEventListener('hashchange', onChange)
  }
}

function readHash(): string {
  return window.location.hash
}

/**
 * The current route.
 *
 * `knownAreaIds` and `fallbackAreaId` have to be stable — they are module-level constants in
 * practice — so that a route object is only rebuilt when the address actually changed.
 */
export function useRoute(knownAreaIds: readonly string[], fallbackAreaId: string): Route {
  const hash = useSyncExternalStore(subscribe, readHash, () => '')
  return useMemo(
    () => parseRoute(hash, knownAreaIds, fallbackAreaId),
    [hash, knownAreaIds, fallbackAreaId],
  )
}

export interface NavigateOptions {
  /** Replaces the current entry instead of adding one — for correcting an address, not for moving. */
  readonly replace?: boolean
}

export function navigate(
  areaId: string,
  path: readonly string[] = [],
  options: NavigateOptions = {},
): void {
  const href = hrefFor(areaId, path)

  if (window.location.hash === href) {
    return
  }

  if (options.replace === true) {
    window.history.replaceState(null, '', href)
    // `replaceState` changes the address without telling anybody. The listeners are the point.
    window.dispatchEvent(new HashChangeEvent('hashchange'))
    return
  }

  window.location.hash = href
}

/**
 * What a link to a route should do when it is clicked with nothing held down.
 *
 * Modified clicks and anything but the primary button are left to the browser, so opening an area
 * in a new tab keeps working.
 */
export function isPlainClick(event: MouseEvent): boolean {
  return (
    !event.defaultPrevented &&
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  )
}
